import { useEffect, useState } from 'react';

// Visitors outside India see Ownezzy's plan prices in US dollars too, next to the rupee price. It is an estimate at
// the day's rate (GET /api/plans/usd-rate): plans are always charged in rupees and the buyer's bank converts.
// India is told apart by the visitor's country (from their IP address: GET /api/plans/visitor, kept for a day by
// loadVisitorCountry), else by the browser's time zone; ?currency=usd or ?currency=inr in the address forces either.
const COUNTRY_KEY = 'oz_visitor_country';
const DAY = 24 * 60 * 60 * 1000;
const savedCountry = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(COUNTRY_KEY) || 'null');
    return saved && Date.now() - saved.at < DAY ? saved.country : '';
  } catch {
    return '';
  }
};

// Looks the visitor's country up (at most once a day) before the page draws, so the lists are in the right order from
// the start. Resolves within `wait` ms even when the lookup is slow; a later visit uses the saved country at once.
export function loadVisitorCountry(apiBase = '/api', wait = 800) {
  if (savedCountry()) return Promise.resolve();
  const lookup = fetch(`${apiBase}/plans/visitor`)
    .then((res) => res.json())
    .then((data) => {
      if (/^[A-Z]{2}$/.test(data.country || '') && data.country !== 'XX') localStorage.setItem(COUNTRY_KEY, JSON.stringify({ country: data.country, at: Date.now() }));
    })
    .catch(() => {});
  return Promise.race([lookup, new Promise((resolve) => setTimeout(resolve, wait))]);
}

export function isInternational() {
  try {
    const forced = new URLSearchParams(window.location.search).get('currency');
    if (forced) return forced.toLowerCase() === 'usd';
    const country = savedCountry();
    if (country) return country !== 'IN';
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return Boolean(zone) && !/^Asia\/(Kolkata|Calcutta)$/.test(zone);
  } catch {
    return false;
  }
}

const FALLBACK_RATE = 0.0113; // about ₹88.5 per US$, until the day's rate loads
let ratePromise = null;

// { on, usd, price, charged } for an amount in rupees; on is true for visitors outside India.
//   usd(399)     -> "≈ US$4.52" abroad, "" in India
//   price(399)   -> "US$4.52" abroad, "₹399" in India: plan prices shown in the visitor's currency
//   charged(399) -> "Charged as ₹399" abroad, "" in India: payment is always taken in rupees
export function useUsd(apiBase = '/api') {
  const [on] = useState(isInternational);
  const [rate, setRate] = useState(FALLBACK_RATE);
  useEffect(() => {
    if (!on) return undefined;
    let live = true;
    ratePromise = ratePromise || fetch(`${apiBase}/plans/usd-rate`).then((res) => res.json()).then((data) => Number(data.rate) || FALLBACK_RATE).catch(() => FALLBACK_RATE);
    ratePromise.then((value) => live && setRate(value));
    return () => { live = false; };
  }, [on, apiBase]);
  const dollars = (rupees) => (Number(rupees) > 0 ? `US$${(Number(rupees) * rate).toFixed(2)}` : 'US$0');
  const usd = (rupees) => (on && Number(rupees) > 0 ? `≈ ${dollars(rupees)}` : '');
  const price = (rupees) => (on ? dollars(rupees) : `₹${Number(rupees || 0).toLocaleString('en-IN')}`);
  const charged = (rupees) => (on && Number(rupees) > 0 ? `Charged as ₹${Number(rupees).toLocaleString('en-IN')}` : '');
  return { on, usd, price, charged };
}
